import { useEffect, useState } from 'react';
import type { PasswordRecord } from '../types';
import { deletePassword, listPasswords, putPassword } from '../lib/db';
import { decryptSecret, encryptSecret, randomPassword } from '../lib/crypto';
import { uid } from '../lib/ids';

export function PasswordManager() {
  const [master, setMaster] = useState('');
  const [records, setRecords] = useState<PasswordRecord[]>([]);
  const [origin, setOrigin] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [revealed, setRevealed] = useState<Record<string, string>>({});
  const [message, setMessage] = useState('');

  const reload = () => listPasswords().then(setRecords);
  useEffect(() => { reload(); }, []);

  async function save() {
    if (!master || !origin || !username || !password) { setMessage('Master password, origin, username and password are required.'); return; }
    const encrypted = await encryptSecret(password, master);
    const now = Date.now();
    await putPassword({ id: uid('pwd'), origin, username, encryptedPassword: encrypted.encrypted, iv: encrypted.iv, salt: encrypted.salt, iterations: encrypted.iterations, createdAt: now, updatedAt: now });
    setPassword(''); setUsername(''); setOrigin(''); setMessage('Password saved encrypted in IndexedDB.'); reload();
  }

  async function reveal(record: PasswordRecord) {
    if (!master) { setMessage('Enter the master password first.'); return; }
    try {
      const plain = await decryptSecret(record.encryptedPassword, master, record.iv, record.salt, record.iterations);
      setRevealed((current) => ({ ...current, [record.id]: plain }));
    } catch {
      setMessage('Could not decrypt. Check the master password.');
    }
  }

  return (
    <section className="internal-page">
      <header className="page-header"><div><p className="eyebrow">websurf://passwords</p><h1>Password Manager</h1></div><span>PBKDF2 + AES-GCM encrypted vault.</span></header>
      <div className="manager-grid">
        <aside className="editor-card">
          <h2>Vault unlock</h2>
          <input type="password" value={master} onChange={(event) => setMaster(event.target.value)} placeholder="Master password" />
          <h2>Add login</h2>
          <input value={origin} onChange={(event) => setOrigin(event.target.value)} placeholder="Origin, e.g. https://example.com" />
          <input value={username} onChange={(event) => setUsername(event.target.value)} placeholder="Username" />
          <div className="inline-input"><input type="password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="Password" /><button onClick={() => setPassword(randomPassword())}>Generate</button></div>
          <button onClick={save}>Encrypt & save</button>
          {message && <p className="helper-copy">{message}</p>}
        </aside>
        <div className="list-card">
          {records.map((record) => (
            <div className="list-row" key={record.id}>
              <div className="row-main"><strong>{record.origin}</strong><span>{record.username}</span><code>{revealed[record.id] ? revealed[record.id] : '••••••••••••'}</code></div>
              <button onClick={() => reveal(record)}>Reveal</button>
              <button onClick={() => deletePassword(record.id).then(reload)}>Delete</button>
            </div>
          ))}
          {records.length === 0 && <p className="empty-copy">No saved passwords yet.</p>}
        </div>
      </div>
    </section>
  );
}
